<template>
  <div class="file-row d-flex align-center">
    <DocumentIconBadge icon="description" class="file-badge" />
    <div class="file-meta flex-grow-1">
      <div class="filename text-body-2 font-weight-bold text-truncate">
        {{ file.friendlyFilename }}
      </div>
      <div class="text-caption text--secondary">
        {{ formattedSize }} · Uploaded {{ formattedDate }}
      </div>
    </div>
    <div class="file-divider"></div>
    <div class="file-actions d-flex align-center">
      <omk-icon-button
        button-type="icon-button"
        aria-label="Download file"
        class="action-icon-btn action-icon-btn--plain"
        data-test="download-file"
        :disabled="downloading"
        @click="download"
      >
        <omk-icon>download</omk-icon>
      </omk-icon-button>
      <DocumentDeleteButton
        v-if="!readOnly"
        aria-label="Delete file"
        data-test="delete-file"
        :disabled="deleting"
        @click="deleteFile"
      />
    </div>
  </div>
</template>

<script>
  import {
    CREATE_TESTATOR_DOCUMENT_FILE_PRESIGNED_URL_MUTATION,
    DELETE_TESTATOR_DOCUMENT_FILE_MUTATION,
  } from '../../constants/graphql/testator-documents';
  import DocumentIconBadge from './DocumentIconBadge.vue';
  import DocumentDeleteButton from './DocumentDeleteButton.vue';

  const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  export default {
    name: 'DocumentFileRow',
    components: { DocumentIconBadge, DocumentDeleteButton },
    props: {
      file: { type: Object, required: true },
      readOnly: { type: Boolean, default: false },
    },
    data() {
      return {
        downloading: false,
        deleting: false,
      };
    },
    computed: {
      formattedSize() {
        const bytes = this.file.byteSize;
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
      },
      formattedDate() {
        const raw = this.file.createdAt;
        if (!raw) return '';
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return '';
        return `${String(d.getDate()).padStart(2, '0')} ${
          MONTHS[d.getMonth()]
        } ${d.getFullYear()}`;
      },
    },
    methods: {
      async download() {
        this.downloading = true;
        try {
          const { data } = await this.$apollo.mutate({
            mutation: CREATE_TESTATOR_DOCUMENT_FILE_PRESIGNED_URL_MUTATION,
            variables: { id: this.file.id },
          });
          const link = document.createElement('a');
          link.href = data.createTestatorDocumentFilePresignedUrl;
          link.download = this.file.friendlyFilename || '';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } finally {
          this.downloading = false;
        }
      },
      async deleteFile() {
        this.deleting = true;
        try {
          await this.$apollo.mutate({
            mutation: DELETE_TESTATOR_DOCUMENT_FILE_MUTATION,
            variables: { id: this.file.id },
          });
          this.$emit('file-deleted', { id: this.file.id });
        } finally {
          this.deleting = false;
        }
      },
    },
  };
</script>

<style scoped>
  .file-row {
    gap: 16px;
    padding: 16px 16px 16px 20px;
    background: #f9fafb;
    border: 1px solid #d9d9d9;
    border-radius: 10px;
  }
  .file-row + .file-row {
    margin-top: 8px;
  }
  .file-meta {
    min-width: 0;
  }
  .file-actions {
    gap: 10px;
    flex-shrink: 0;
  }
  .action-icon-btn--plain {
    --omk-icon-button-icon-color: var(
      --omk-colour-icon-active-resting,
      #306e2b
    );
    --omk-icon-button-hover-icon-color: var(
      --omk-colour-icon-active-hover,
      #20491d
    );
  }
  .file-divider {
    display: none;
    align-self: stretch;
    width: 1px;
    background: #d9d9d9;
  }
  @media (max-width: 600px) {
    .file-badge {
      display: none;
    }
    .file-divider {
      display: block;
    }
  }
  .filename {
    color: #111827;
    font-family: 'Montserrat', sans-serif !important;
  }
  .action-icon-btn {
    flex-shrink: 0;
    --omk-icon-button-regular-size: 36px;
  }
</style>
